import React from 'react';
import PartNumbersList from '../../../shared/components/PartNumbersList';
import DispositionGapBadge from './DispositionGapBadge';
import PhotoGalleryPanel from '../../../shared/components/PhotoGalleryPanel';
import { flattenPartNumbers } from '../utils/flattenPartNumbers';
import './DispositionDetailPane.css';

/* ── Field grid spec (label, key) ──────────────────────────────────── */
const FIELD_DEFS = [
  { label: 'Tool description',  key: 'tool_description' },
  { label: 'Part description',  key: 'part_description' },
  { label: 'Dimensions',        key: 'dimensions' },
  { label: 'Weight',            key: 'weight_display' },
  { label: 'Material',          key: 'material' },
  { label: 'Platform',          key: 'platform' },
  { label: 'Last ship date',    key: 'last_ship_date' },
  { label: 'Retained reason',   key: 'retained_reason' },
];

function FieldValue({ value }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <span className={`dispositions-field-value${empty ? ' empty' : ''}`}>
      {empty ? '—' : value}
    </span>
  );
}


export default function DispositionDetailPane({ tool }) {
  /* ── Empty state ─────────────────────────────────────────────────── */
  if (!tool) {
    return (
      <div className="dispositions-detail-empty">
        <div className="dispositions-detail-empty-inner">
          <div className="dispositions-detail-empty-icon" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </div>
          <p className="dispositions-detail-empty-title">
            Select a tool to view disposition details.
          </p>
        </div>
      </div>
    );
  }

  const flatPartNumbers = flattenPartNumbers(tool.part_numbers);
  const missingCritical = Array.isArray(tool.missing_critical) ? tool.missing_critical : [];
  const missingOther    = Array.isArray(tool.missing_other)    ? tool.missing_other    : [];
  const asksOfSupplier  = Array.isArray(tool.asks_of_supplier) ? tool.asks_of_supplier : [];
  const assetTags       = Array.isArray(tool.asset_tags)       ? tool.asset_tags       : [];

  return (
    <div className="dispositions-detail">
      {/* ── (a) Header bar ──────────────────────────────────────────── */}
      <header className="dispositions-detail-header">
        <h2 className="dispositions-detail-id">
          {tool.supplier_tool_id || tool.tool_id || '—'}
        </h2>
        <div className="dispositions-detail-header-meta">
          {tool.tool_type && (
            <span className="dispositions-detail-pill">{tool.tool_type}</span>
          )}
          {tool.oem && (
            <span className="dispositions-detail-pill dispositions-detail-pill-oem">
              {tool.oem}
            </span>
          )}
          <DispositionGapBadge level={tool.gap_level} />
        </div>
      </header>

      {/* ── (b) Photo gallery ──────────────────────────────────────── */}
      <section className="dispositions-detail-section">
        <h3 className="dispositions-detail-section-title">
          Photos {tool.photo_urls?.length ? `(${tool.photo_urls.length})` : ''}
        </h3>
        <PhotoGalleryPanel photoUrls={tool.photo_urls} layout="grid" />
      </section>

      {/* ── (c) Field grid ─────────────────────────────────────────── */}
      <section className="dispositions-detail-section">
        <h3 className="dispositions-detail-section-title">Details</h3>
        <dl className="dispositions-field-grid">
          {FIELD_DEFS.map(f => (
            <div className="dispositions-field" key={f.key}>
              <dt className="dispositions-field-label">{f.label}</dt>
              <dd>
                <FieldValue value={tool[f.key]} />
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ── (d) Gap analysis ───────────────────────────────────────── */}
      <section className="dispositions-detail-section">
        <h3 className="dispositions-detail-section-title">Gap analysis</h3>

        {tool.submittable_basis && (
          <p className="dispositions-gap-block-empty">
            Submittable basis: {tool.submittable_basis === 'DWELL_EXCEEDED' ? 'Dwell exceeded' : 'Criteria met'}
          </p>
        )}

        <div className="dispositions-gap-block dispositions-gap-block-critical">
          <div className="dispositions-gap-block-title">Required to submit</div>
          {missingCritical.length === 0 ? (
            <p className="dispositions-gap-block-empty">All required fields populated.</p>
          ) : (
            <ul className="dispositions-gap-block-list">
              {missingCritical.map((label, i) => (
                <li key={i}>{label}</li>
              ))}
            </ul>
          )}
        </div>

        {missingOther.length > 0 && (
          <div className="dispositions-gap-block dispositions-gap-block-partial">
            <div className="dispositions-gap-block-title">Recommended fields</div>
            <ul className="dispositions-gap-block-list">
              {missingOther.map((label, i) => (
                <li key={i}>{label}</li>
              ))}
            </ul>
          </div>
        )}

        {asksOfSupplier.length > 0 && (
          <div className="dispositions-gap-block dispositions-gap-block-asks">
            <div className="dispositions-gap-block-title">
              Information needed from supplier
            </div>
            <ul className="dispositions-gap-block-list">
              {asksOfSupplier.map((ask, i) => (
                <li key={i}>{ask}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* ── (e) Asset tags ─────────────────────────────────────────── */}
      <section className="dispositions-detail-section">
        <h3 className="dispositions-detail-section-title">
          Asset tags {assetTags.length > 0 ? `(${assetTags.length})` : ''}
        </h3>
        {assetTags.length === 0 ? (
          <p className="dispositions-detail-section-empty">No asset tags.</p>
        ) : (
          <div className="dispositions-tag-chips">
            {assetTags.map((tag, i) => (
              <span key={i} className="dispositions-tag-chip">
                {tag.tag_value}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* ── (f) Part numbers ───────────────────────────────────────── */}
      <section className="dispositions-detail-section">
        <h3 className="dispositions-detail-section-title">
          Part numbers {flatPartNumbers.length > 0 ? `(${flatPartNumbers.length})` : ''}
        </h3>
        <PartNumbersList partNumbers={flatPartNumbers} readOnly />
      </section>
    </div>
  );
}
